import { of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { SnIntegrationsAPI } from '@lib/api/services';
import { SnCurrentOrganization } from '@lib/auth';
import { t } from '@lib/translation';
import { SentryVaultComponent } from '@lib/vault';

import {
  FsLprFleetRead,
  FleetRecordType,
  IFsLprResult,
  LprIntegrationType,
  SnLprV3FlockRead,
} from '../../api';
import { FleetLprService } from '../../integrations';
import {
  FsLprReportGeneratorService,
  LprReadsAPI,
  PlateImageCropService,
  PlateImageDownloadService,
  PlateReviewService,
} from '../../services';
import { LightboxImage } from '../image-lightbox-viewer/models/lightbox-image.model';
import { QueryResultsMapViewComponent } from '../query-results-map-view/query-results-map-view.component';

@Component({
  selector: 'fs-fleet-query-plate-card',
  templateUrl: './fleet-query-plate-card.component.html',
  styleUrls: ['./fleet-query-plate-card.component.scss'],
})
export class FleetQueryPlateCardComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() public inputPlate: FsLprFleetRead;
  @Input() public query: string;
  @Input() public onMap: boolean;
  @Input() public selected: boolean;
  @Input() public shrunk: boolean;
  @Input() public warningTooltip: string | null = null;
  @Input() public isReviewingPlates: boolean = false;

  @Output() public hideDetails = new EventEmitter<boolean>();

  public activeMedia: any;
  public plate: FsLprFleetRead;
  public canvasFailed: boolean = false;
  public placeHolderImageUrl: string = '/assets/map-icons/No_Image_Found.png';
  public placeHolderSmallImageUrl: string = '/assets/map-icons/No_Image_Found_Small.png';
  public isMarkedForReview: boolean = false;
  private readonly _destroy$ = new Subject<void>();
  public lightboxImages: LightboxImage[] = [];
  public lightboxVisible: boolean = false;
  public hasImageLoadError = false;
  public croppedImageUrl: string | null = null;
  public plateCardId: string;
  public readonly HIT_TYPE = FleetRecordType.HIT;
  public isHit: boolean = false;

  constructor(
    private readonly _integrationsAPI: SnIntegrationsAPI,
    private readonly _matDialog: MatDialog,
    private readonly _lprReportGeneratorService: FsLprReportGeneratorService,
    private readonly _lprFleetService: FleetLprService,
    private readonly _plateImageDownloadService: PlateImageDownloadService,
    private readonly _elementRef: ElementRef,
    private readonly _lprReadsAPI: LprReadsAPI,
    private readonly _sanitizer: DomSanitizer,
    private readonly _cd: ChangeDetectorRef,
    private readonly _imageCropService: PlateImageCropService,
    private readonly _plateReviewService: PlateReviewService,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {}

  get lprSharingEnabled(): boolean {
    return this._currentOrganization.settings.lprSharingManagementEnabled;
  }

  get plateImageUrl(): string | SafeResourceUrl {
    if (this.plate.plateImageUrl && this.plate.plateImageKey) {
      return this._sanitizer.bypassSecurityTrustResourceUrl(this.plate.plateImageUrl);
    }
    return this.plate.plateImageUrl;
  }

  get hasExternalFlockV3Link(): boolean {
    return (
      this.plate.integratedLprType === LprIntegrationType.FLOCK_V3 &&
      !!(this.plate as IFsLprResult as SnLprV3FlockRead).imageUrl
    );
  }

  get shouldShowDownloadButton(): boolean {
    return !this.hasExternalFlockV3Link;
  }

  get hitTooltipText(): string {
    return this.plate.hitReason || t('alpr.alerts.hitIndicator');
  }

  get sharingAgencyNameTooltip(): string {
    return t('vehicleIntelligence.alprSearch.form.sharedRecords.sharingAgencyTooltip', { agencyName: this.plate.agencyInfo?.name || '' })
  }

  public openExternalFlockV3Link(event: Event): void {
    event.stopPropagation();
    if (this.hasExternalFlockV3Link) {
      window.open((this.plate as IFsLprResult as SnLprV3FlockRead).imageUrl, '_blank');
    }
  }

  @HostListener('document:keydown.space', ['$event'])
  onSpacePressed(event: KeyboardEvent): void {
    // Only handle keyboard events when this component's element is focused
    if (!this._elementRef.nativeElement.parentElement.contains(document.activeElement)) {
      return;
    }
    event.preventDefault();
    if (this.selected) {
      this.toggleMarkForReview();
    }
  }

  public ngOnInit(): void {
    this.plate = this.inputPlate;
    this.plateCardId = `plate-card-${this.plate.id}`;
    this.determineContextImage();
    this.isMarkedForReview = this._plateReviewService.isMarkedForReview(this.plate.id);
    this.isHit = this.plate.isHit || this.plate.type === this.HIT_TYPE;
    this._plateReviewService
      .getReviewedPlates()
      .pipe(takeUntil(this._destroy$))
      .subscribe((plates) => {
        this.isMarkedForReview = plates.some((plate) => plate.id === this.plate.id);
        this._cd.detectChanges();
      });
  }

  public ngAfterViewInit(): void {
    if (this.activeMedia?.url) {
      this._resizeContextImage();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.croppedImageUrl = null;
  }

  // Determines the context image to be used for the plate card from the plate object
  public determineContextImage(): void {
    if (this.plate.plateImageKey) {
      this._getVigilantPlateImage(this.plate.plateImageKey, (blob) => {
        this.plate.plateImageUrl = URL.createObjectURL(blob);
      });
    }
    if (this.plate.overviewImageKey) {
      this._getVigilantPlateImage(this.plate.overviewImageKey, (blob) => {
        this.plate.contextImageUrl = URL.createObjectURL(blob);
        this.activeMedia = { url: this._getSanitizedUrl(blob), type: 'context' };
        this._resizeContextImage();
        this._cd.detectChanges();
      });
    } else {
      this.activeMedia = { url: this.placeHolderImageUrl, type: 'context' };
    }
    if (this.plate.contextImageUrl) {
      this.activeMedia = { url: this.plate.contextImageUrl, type: 'context' };
      // Add timeout to ensure DOM is ready
      setTimeout(() => {
        this._resizeContextImage();
      });
    }
  }

  public setPlaceholderImage(event: UIEvent): void {
    if (event.target instanceof HTMLImageElement) {
      event.target.src = this.placeHolderImageUrl;
      this.hasImageLoadError = true;
      this._cd.detectChanges();
    }
  }

  public setPlatePlaceholderImage(event: any): void {
    event.target.src = this.placeHolderSmallImageUrl;
  }

  public downloadImage(plate: FsLprFleetRead): void {
    this.croppedImageUrl = this.croppedImageUrl;
    this._plateImageDownloadService.downloadImage(plate, this.croppedImageUrl);
  }

  /**
   * Adds or removes the plate from the review list
   */
  public toggleMarkForReview(): void {
    if (this.isMarkedForReview) {
      this.isMarkedForReview = false;
      this._plateReviewService.removeFromReview(this.plate.id);
    } else {
      this.isMarkedForReview = true;
      this._plateReviewService.markForReview(this.plate);
    }
  }

  /**
   * Opens the lightbox with the cropped image if available
   */
  public viewLarger(): void {
    if (!this.activeMedia?.url) {
      return;
    }

    const image: LightboxImage = {
      url: this.activeMedia.url,
    };
    const images = [image];

    // Add the cropped image if available
    if (this.croppedImageUrl && !this.canvasFailed) {
      const croppedImage: LightboxImage = {
        url: this.croppedImageUrl,
        class: 'cropped-image',
      };
      images.unshift(croppedImage); // Add cropped image as first image
    }

    this.hideDetails.emit(true);
    this.lightboxImages = images;
    this.lightboxVisible = true;
  }

  public closeLightbox(): void {
    this.lightboxVisible = false;
    this.hideDetails.emit(false);
  }

  public showOnMap(value: any): void {
    const mapResults = this._matDialog.open(QueryResultsMapViewComponent, {
      data: value,
      width: '75vw',
      hasBackdrop: true,
    });
    mapResults
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  public sendToVault(plate: FsLprFleetRead): void {
    this._lprReportGeneratorService.generatePdfReport(plate).then((blob) => {
      const now = new Date();
      const file = new File([blob], `ALPR_Report_${plate.plate}_${now.getTime()}.pdf`, {
        type: blob.type,
      });
      const formData = new FormData();
      formData.append('files', file, file.name);
      const dialog = this._matDialog.open(SentryVaultComponent);
      dialog
        .afterClosed()
        .pipe(takeUntil(this._destroy$))
        .subscribe((vaultID) => {
          const vault = vaultID?.id || vaultID;
          if (vault) {
            this._addToVault(vault, formData);
          }
        });
    });
  }

  private _addToVault(vault: string, formData: any): void {
    this._lprFleetService.addMediaToVault(vault, formData).subscribe();
  }

  private _getVigilantPlateImage(imageKey: string, callback: (imageBlob: Blob) => void): void {
    this._lprReadsAPI
      .getVigilantPlateImage(imageKey)
      .pipe(
        catchError(() => {
          this.canvasFailed = true;
          this.activeMedia = { url: this.placeHolderImageUrl, type: 'context' };
          this.plate.plateImageUrl = this.placeHolderImageUrl;
          return of(null);
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((blob: Blob) => {
        if (blob) {
          callback(blob);
        }
      });
  }

  private _getSanitizedUrl(blob: Blob): SafeResourceUrl {
    return this._sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
  }

  // The functions below calculate the bounding box of the plate and extend the crop
  // Referenced from fleet documentation: https://axon.quip.com/aI99A8RYVEN0#JLMAAADPM5l
  private _resizeContextImage(): void {
    const canvas: HTMLCanvasElement = document.getElementById(
      'context-' + this.plate.id,
    ) as HTMLCanvasElement;

    this._imageCropService
      .cropImage(
        this.plate,
        canvas,
        PlateImageCropService.CARD_DIMENSIONS,
        false, // Do NOT show bounding box in card view
      )
      .then((croppedUrl) => {
        this.croppedImageUrl = croppedUrl;
        this._cd.detectChanges();
      })
      .catch((err) => {
        this.croppedImageUrl = null;
        this.canvasFailed = true;
        this._cd.detectChanges();
      });
  }
}
